/**
 * Enhanced API utility with caching, request deduplication, and performance monitoring
 * Includes runtime optimizations based on network conditions and device capabilities
 */
import api from '../api/axios';
import { apiCache } from './cacheManager';
import { performanceMonitor } from './performanceMonitor';

// Constants for environment-aware configuration
const ENV = {
  isProd: process.env.NODE_ENV === 'production',
  isDev: process.env.NODE_ENV === 'development'
};

/**
 * Creates a cache key from request parameters
 * @param {string} url - The URL of the request
 * @param {Object} params - The parameters of the request
 * @returns {string} - A cache key
 */
const createCacheKey = (url, params = {}) => {
  return `${url}:${JSON.stringify(params)}`;
};

/**
 * Request priority levels for runtime optimization
 */
const RequestPriority = {
  CRITICAL: 'critical',  // User-blocking, needs immediate response
  HIGH: 'high',          // Important for user experience
  NORMAL: 'normal',      // Standard priority
  LOW: 'low',            // Background tasks, can be delayed
  PREFETCH: 'prefetch'   // Preemptive loading, lowest priority
};

/**
 * Manages pending request queues for prioritization
 */
class RequestQueue {
  constructor() {
    this.queues = {
      [RequestPriority.CRITICAL]: [],
      [RequestPriority.HIGH]: [],
      [RequestPriority.NORMAL]: [],
      [RequestPriority.LOW]: [],
      [RequestPriority.PREFETCH]: []
    };
    this.isProcessing = false;
    this.maxConcurrent = 6;  // Default max concurrent requests
    this.activeRequests = 0;
  }
  
  /**
   * Add a request to the appropriate priority queue
   * @param {Object} request - Request object with execute function and priority
   * @returns {Promise} - Promise that resolves with the request result
   */
  add(request) {
    return new Promise((resolve, reject) => {
      const queueItem = {
        ...request,
        resolve,
        reject
      };
      
      this.queues[request.priority].push(queueItem);
      this.processQueues();
    });
  }
  
  /**
   * Process queues in priority order
   */
  async processQueues() {
    // Skip if already processing or no active requests
    if (this.isProcessing || this.activeRequests >= this.maxConcurrent) {
      return;
    }
    
    this.isProcessing = true;
    
    // Process queues in priority order
    for (const priority of Object.keys(this.queues)) {
      const queue = this.queues[priority];
      
      while (queue.length > 0 && this.activeRequests < this.maxConcurrent) {
        const request = queue.shift();
        this.activeRequests++;
        
        try {
          // Execute the request and resolve its promise
          const result = await request.execute();
          request.resolve(result);
        } catch (error) {
          request.reject(error);
        } finally {
          this.activeRequests--;
        }
      }
      
      // If we've hit max concurrent requests, break out
      if (this.activeRequests >= this.maxConcurrent) {
        break;
      }
    }
    
    this.isProcessing = false;
    
    // If we still have capacity and queued requests, continue processing
    if (this.activeRequests < this.maxConcurrent && 
        Object.values(this.queues).some(q => q.length > 0)) {
      this.processQueues();
    }
  }
  
  /**
   * Adjust max concurrent requests based on network conditions
   * @param {Object} networkInfo - Network information
   */
  adjustConcurrency(networkInfo) {
    // Default is 6 concurrent requests
    let newMax = 6;
    
    // Adjust based on connection type if available
    if (networkInfo && typeof networkInfo === 'object') {
      if (networkInfo.effectiveType === 'slow-2g' || networkInfo.saveData) {
        newMax = 2;  // Reduce to 2 for very slow connections
      } else if (networkInfo.effectiveType === '2g') {
        newMax = 3;  // Reduce to 3 for 2g
      } else if (networkInfo.effectiveType === '3g') {
        newMax = 4;  // Reduce to 4 for 3g
      } else if (networkInfo.effectiveType === '4g' && networkInfo.downlink > 5) {
        newMax = 8;  // Increase to 8 for fast 4g
      }
    }
    
    this.maxConcurrent = newMax;
  }
}

// Create a singleton request queue
const requestQueue = new RequestQueue();

// Periodically update request concurrency based on network conditions
if (typeof window !== 'undefined' && apiCache.networkCondition) {
  setInterval(() => {
    requestQueue.adjustConcurrency(apiCache.networkCondition);
  }, 30000); // Check every 30 seconds
}

const enhancedApi = {
  /**
   * Make a GET request with caching
   * @param {string} url - The URL to request
   * @param {Object} options - Request options
   * @param {Object} options.params - Query parameters
   * @param {number} options.cacheTTL - Cache TTL in ms (0 to skip cache)
   * @param {boolean} options.forceRefresh - Force refresh even if cached
   * @param {string} options.priority - Request priority (critical, high, normal, low, prefetch)
   * @param {boolean} options.useConditionalRequest - Use ETag/If-Modified-Since headers
   * @param {boolean} options.useDeduplication - Deduplicate identical requests
   * @returns {Promise<any>} - Response data
   */
  async get(url, options = {}) {
    const { 
      params = {}, 
      cacheTTL = apiCache.defaultTTL, 
      forceRefresh = false,
      priority = RequestPriority.NORMAL,
      useConditionalRequest = ENV.isProd, // Default to enabled in production
      useDeduplication = true
    } = options;
    
    const cacheKey = createCacheKey(url, params);
    
    // Check cache first (if not forcing refresh and TTL > 0)
    if (!forceRefresh && cacheTTL > 0) {
      const cachedData = apiCache.get(cacheKey);
      if (cachedData) {
        // Only log in development mode to reduce console noise
        if (ENV.isDev) {
          console.log(`🔄 Cache hit for ${url}`);
        }
        return cachedData;
      }
    }
    
    // Prepare headers for conditional requests
    const headers = {};
    
    // Add conditional request headers if enabled and we have cached metadata
    if (useConditionalRequest && !forceRefresh) {
      const cachedMetadata = apiCache.get(`${cacheKey}:metadata`);
      if (cachedMetadata) {
        if (cachedMetadata.etag) {
          headers['If-None-Match'] = cachedMetadata.etag;
        }
        if (cachedMetadata.lastModified) {
          headers['If-Modified-Since'] = cachedMetadata.lastModified;
        }
      }
    }
    
    // Deduplicate requests if enabled
    const fetchFunction = async () => {
      // Measure performance
      return performanceMonitor.measure(async () => {
        try {
          const response = await api.get(url, { 
            params,
            headers
          });
          
          // Handle 304 Not Modified responses
          if (response.status === 304) {
            // Return the cached data since it's still valid
            const cachedData = apiCache.get(cacheKey);
            if (cachedData) {
              return cachedData;
            }
          }
          
          // Cache the result if TTL > 0
          if (cacheTTL > 0) {
            // Store the actual data
            apiCache.set(cacheKey, response.data, cacheTTL);
            
            // Store metadata for conditional requests
            if (useConditionalRequest) {
              apiCache.set(`${cacheKey}:metadata`, {
                etag: response.headers.etag,
                lastModified: response.headers['last-modified']
              }, cacheTTL);
            }
          }
          
          return response.data;
        } catch (error) {
          // Special handling for offline mode
          if (!navigator.onLine) {
            // Try to return stale cached data if available
            const cachedData = apiCache.get(cacheKey);
            if (cachedData) {
              console.log(`🔌 Offline mode: Using cached data for ${url}`);
              return cachedData;
            }
          }
          
          throw error;
        }
      }, `GET ${url}`, 'api');
    };
    
    // Create a request function to be queued or executed
    const executeRequest = async () => {
      // If deduplication is enabled, use the dedupRequest method
      if (useDeduplication) {
        return apiCache.dedupRequest(cacheKey, fetchFunction);
      } else {
        return fetchFunction();
      }
    };
    
    // Use the request queue for prioritization in production
    if (ENV.isProd) {
      return requestQueue.add({
        priority,
        execute: executeRequest
      });
    } else {
      // Skip prioritization in development for simpler debugging
      return executeRequest();
    }
  },
  
  /**
   * Make a POST request with optional caching
   * @param {string} url - The URL to request
   * @param {Object} data - Request body
   * @param {Object} options - Request options
   * @param {number} options.cacheTTL - Cache TTL in ms (0 to skip cache)
   * @param {string} options.priority - Request priority (critical, high, normal, low)
   * @param {boolean} options.invalidateCache - Invalidate related cache entries
   * @param {Array<string>} options.invalidatePatterns - Cache key patterns to invalidate
   * @returns {Promise<any>} - Response data
   */
  async post(url, data = {}, options = {}) {
    const { 
      cacheTTL = 0, // Default to no caching for POST requests
      useDeduplication = true,
      priority = RequestPriority.HIGH, // POST requests are higher priority by default
      invalidateCache = true, // Invalidate by default
      invalidatePatterns = [] // Specific patterns to invalidate
    } = options;
    
    const cacheKey = `POST:${url}:${JSON.stringify(data)}`;
    
    const fetchFunction = async () => {
      return performanceMonitor.measure(async () => {
        try {
          const response = await api.post(url, data);
          
          // Cache the result if TTL > 0
          if (cacheTTL > 0) {
            apiCache.set(cacheKey, response.data, cacheTTL);
          }
          
          // Handle cache invalidation for mutation operations
          if (invalidateCache) {
            this.invalidateRelatedCaches(url, invalidatePatterns);
          }
          
          return response.data;
        } catch (error) {
          // Special handling for offline mode - queue the request for later
          if (!navigator.onLine && ENV.isProd) {
            console.log(`🔌 Offline mode: Queueing POST request to ${url} for later`);
            // You could implement an offline queue system here
            // For now, just rethrow the error as a proper Error object
          }
          
          throw error instanceof Error ? error : new Error(error);
        }
      }, `POST ${url}`, 'api');
    };
    
    // Create a request function to be queued or executed
    const executeRequest = async () => {
      // If deduplication is enabled, use the dedupRequest method
      if (useDeduplication) {
        return apiCache.dedupRequest(cacheKey, fetchFunction);
      } else {
        return fetchFunction();
      }
    };
    
    // Use the request queue for prioritization in production
    if (ENV.isProd) {
      return requestQueue.add({
        priority,
        execute: executeRequest
      });
    } else {
      // Skip prioritization in development for simpler debugging
      return executeRequest();
    }
  },
  
  /**
   * Invalidate related cache entries when a mutation occurs
   * @param {string} url - The URL of the mutation operation
   * @param {Array<string>} additionalPatterns - Additional cache key patterns to invalidate
   */
  invalidateRelatedCaches(url, additionalPatterns = []) {
    // Skip in development to make debugging easier
    if (!ENV.isProd) return;
    
    // Default patterns based on URL
    const patterns = [];
    
    // Derive patterns from URL
    if (url.includes('/jobs/')) {
      patterns.push('/api/jobs/fetchjobs');
    } else if (url.includes('/companies/')) {
      patterns.push('/api/companies/fetchcompanies');
    } else if (url.includes('/profile/')) {
      patterns.push('/api/profile/');
    }
    
    // Add user-specified patterns
    if (additionalPatterns.length > 0) {
      patterns.push(...additionalPatterns);
    }
    
    // No patterns to invalidate
    if (patterns.length === 0) return;
    
    // Find matching cache keys
    const keysToInvalidate = [];
    
    // Get all cache keys
    apiCache.cache.forEach((_, key) => {
      // Check if key matches any pattern
      if (patterns.some(pattern => key.includes(pattern))) {
        keysToInvalidate.push(key);
      }
    });
    
    // Invalidate matching keys
    keysToInvalidate.forEach(key => {
      apiCache.delete(key);
    });
    
    // Log in production (but only if significant)
    if (keysToInvalidate.length > 0 && ENV.isProd) {
      console.log(`🧹 Invalidated ${keysToInvalidate.length} cache entries related to ${url}`);
    }
  },
  
  /**
   * Clear all API cache entries
   */
  clearCache() {
    apiCache.clear();
  },
  
  /**
   * Clear a specific cache entry
   * @param {string} url - The URL of the cache entry to clear
   * @param {Object} params - The parameters of the cache entry
   */
  clearCacheFor(url, params = {}) {
    const cacheKey = createCacheKey(url, params);
    apiCache.delete(cacheKey);
  },
  
  /**
   * Get runtime optimization status
   * @returns {Object} - Current runtime optimization status
   */
  getRuntimeOptimizationStatus() {
    return {
      networkCondition: apiCache.networkCondition || 'unknown',
      deviceCapability: apiCache.deviceCapability || 'unknown',
      apiCacheSize: apiCache.cache.size || 0,
      pendingRequests: apiCache.pendingRequests.size || 0,
      requestQueueStatus: {
        activeRequests: requestQueue.activeRequests,
        maxConcurrent: requestQueue.maxConcurrent,
        queueSizes: {
          critical: requestQueue.queues[RequestPriority.CRITICAL].length,
          high: requestQueue.queues[RequestPriority.HIGH].length,
          normal: requestQueue.queues[RequestPriority.NORMAL].length,
          low: requestQueue.queues[RequestPriority.LOW].length,
          prefetch: requestQueue.queues[RequestPriority.PREFETCH].length
        }
      },
      isProduction: ENV.isProd
    };
  }
};

export default enhancedApi;